import {
  generateReportProprietaMisurata,
  GetDispositivoHistoryDataParams,
  getGetDispositivoAnalisiDataQueryOptions,
  getGetDispositivoHistoryDataQueryOptions,
  NexusOverlayDto,
  NodoDto,
  ProprietaMisurataDto,
} from "@/api";
import Box from "@/elements/Box";
import Button from "@/elements/Button";
import {
  getDispositivoTypeLabel,
  TipoDispositivoByTipoNodo,
} from "@/utils/dispositivoUtils";
import { sortProprietaMisurate } from "@/utils/proprietaMisurataUtils";
import { withOpeningReset } from "@/utils/withOpeningReset";
import {
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  FormLabel,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import { DateTimeField } from "@mui/x-date-pickers";
import { useQueries, useQuery } from "@tanstack/react-query";
import moment, { Moment } from "moment";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import Loading from "../Loading";
import LoadingError from "../LoadingError";
import ProprietaMisurataHistoryChart, {
  ChartData,
} from "../ProprietaMisurataHistoryChart";
import HistoryIntervalSlider from "./HistoryIntervalSlider";
import DownloadIcon from "@mui/icons-material/Download";
import { toast } from "react-toastify";
import { portaleConfig } from "@/portaliConfig";

type ProprietaMisurataIntervalsHelper = {
  getLabel: (t: TFunction) => string;
  getFrom: () => Moment;
};

const ProprietaMisurataIntervals: {
  [label: string]: ProprietaMisurataIntervalsHelper;
} = {
  "1h": {
    getLabel: (t: TFunction) => t("proprietaMisurataHistory.intervalli.unOra"),
    getFrom: () => moment().subtract(1, "h"),
  },
  "3h": {
    getLabel: (t: TFunction) => t("proprietaMisurataHistory.intervalli.treOre"),
    getFrom: () => moment().subtract(3, "h"),
  },
  "6h": {
    getLabel: (t: TFunction) => t("proprietaMisurataHistory.intervalli.seiOre"),
    getFrom: () => moment().subtract(6, "h"),
  },
  "12h": {
    getLabel: (t: TFunction) =>
      t("proprietaMisurataHistory.intervalli.dodiciOre"),
    getFrom: () => moment().subtract(12, "h"),
  },
  "24h": {
    getLabel: (t: TFunction) =>
      t("proprietaMisurataHistory.intervalli.ventiquattroOre"),
    getFrom: () => moment().subtract(24, "h"),
  },
  "2d": {
    getLabel: (t: TFunction) =>
      t("proprietaMisurataHistory.intervalli.dueGiorni"),
    getFrom: () => moment().subtract(2, "days"),
  },
  "7d": {
    getLabel: (t: TFunction) =>
      t("proprietaMisurataHistory.intervalli.setteGiorni"),
    getFrom: () => moment().subtract(7, "days"),
  },
  "30d": {
    getLabel: (t: TFunction) =>
      t("proprietaMisurataHistory.intervalli.trentaGiorni"),
    getFrom: () => moment().subtract(30, "days"),
  },
};

type RangeOrIntervalType =
  | {
      type: "custom";
      key: string;
      from?: Moment | null;
      to?: Moment | null;
    }
  | {
      type: "interval";
      key: string;
      helper: ProprietaMisurataIntervalsHelper;
    }
  | {
      type: "trasferimento";
      key: string;
      trasferimentoId: number;
    };

function ProprietaMisurataHistoryDialog({
  open = false,
  onClose,
  dispositivo,
  proprietaMisurateInitial,
}: {
  open?: boolean;
  onClose?: () => void;
  dispositivo?: NodoDto;
  proprietaMisurateInitial?: ProprietaMisurataDto[];
}) {
  const { t } = useTranslation();
  const [intervalOrRange, setIntervalOrRange] = useState<RangeOrIntervalType>({
    type: "interval",
    key: "24h",
    helper: ProprietaMisurataIntervals["24h"],
  });
  const [proprietaMisurate, setProprietaMisurate] = useState(
    proprietaMisurateInitial ?? [],
  );

  const [rangeZoom, setRangeZoom] = useState<[string, string] | [null, null]>([
    null,
    null,
  ]);

  // const trasferimenti = useMemo(() => {
  //   if(!dispositivo) {
  //     return []
  //   }

  //   return getAllPathsFromBreadcrumbs(dispositivo, true);
  // },  [dispositivo])

  const selectedTrasferimentoObject = useMemo(() => {
    if (intervalOrRange?.type !== "trasferimento") {
      return null;
    }

    const breadcrumbs =
      dispositivo?.trasferimentiBreadcrumbs?.find((breadcrumbs) => {
        const t = breadcrumbs[breadcrumbs.length - 1];
        return t.arco.id === intervalOrRange?.trasferimentoId;
      }) ?? null;

    if (breadcrumbs) {
      return breadcrumbs[breadcrumbs.length - 1];
    } else {
      return null;
    }
  }, [intervalOrRange, dispositivo?.breadcrumbs]);

  const baseParams = useMemo(() => {
    const params: GetDispositivoHistoryDataParams = {};
    if (intervalOrRange.type === "interval") {
      params._from = intervalOrRange.helper.getFrom().toISOString();
      params._to = moment().toISOString();
    } else if (intervalOrRange.type === "trasferimento") {
      params.idTrasferimento = intervalOrRange.trasferimentoId;
    } else {
      if (intervalOrRange.from && intervalOrRange.to) {
        params._from = intervalOrRange.from?.toISOString();
        params._to = intervalOrRange.to?.toISOString();
      } else {
        return null;
      }
    }
    return params;
  }, [intervalOrRange]);

  const canQueryData = Boolean(open && dispositivo && baseParams);
  const queriesResults = useQueries({
    queries: proprietaMisurate.map((p) => {
      return getGetDispositivoHistoryDataQueryOptions(
        dispositivo?.id ?? 0,
        p.id,
        {
          _from: baseParams?._from,
          _to: baseParams?._to,
          idTrasferimento: baseParams?.idTrasferimento,
        },
        {
          query: {
            enabled: canQueryData,
          },
        },
      );
    }),
  });

  const overlayMisuraId = proprietaMisurate[0]?.id ?? 0;
  const overlayQuery = useQuery(
    getGetDispositivoAnalisiDataQueryOptions(
      dispositivo?.id ?? 0,
      overlayMisuraId,
      {
        _from: baseParams?._from,
        _to: baseParams?._to,
        idTrasferimento: baseParams?.idTrasferimento,
      },
      {
        query: {
          enabled: canQueryData && overlayMisuraId > 0,
        },
      },
    ),
  );

  const [isLoading, error, chartData, nexusOverlay] = useMemo(() => {
    const filterZoom = Boolean(rangeZoom[0] && rangeZoom[1]);
    const from = moment(rangeZoom[0]);
    const to = moment(rangeZoom[1]);

    let atLeastOneLoading = overlayQuery.isLoading;
    let atLeastOneError: any | null = null;
    const overlayResponse = overlayQuery.data as
      | { data?: NexusOverlayDto[] | null }
      | undefined;
    let overlayItems: NexusOverlayDto[] | null = overlayResponse?.data ?? null;

    const data: ChartData[] = [];
    queriesResults.forEach((result, index) => {
      if (result.isLoading) {
        if (result.fetchStatus === "fetching") {
          atLeastOneLoading = true;
        }
      } else if (result.error) {
        if (!atLeastOneError) {
          atLeastOneError = result.error;
        }
      } else {
        const proprietaMisurata = proprietaMisurate[index];

        const d: ChartData = {
          proprietaMisurata: proprietaMisurata,
          values: filterZoom
            ? result.data.data.filter((x) => {
                const m = moment(x.timestamp);
                return m.isSameOrAfter(from) && m.isSameOrBefore(to);
              })
            : result.data.data,
        };
        data.push(d);
      }
    });

    if (overlayQuery.isLoading && overlayQuery.fetchStatus === "fetching") {
      atLeastOneLoading = true;
    } else if (overlayQuery.error && !atLeastOneError) {
      atLeastOneError = overlayQuery.error;
    }

    const filteredOverlay = filterZoom
      ? (overlayItems ?? ([] as NexusOverlayDto[])).filter(
          (x: NexusOverlayDto) => {
            const m = moment(x.timestamp);
            return m.isSameOrAfter(from) && m.isSameOrBefore(to);
          },
        )
      : overlayItems;

    return [atLeastOneLoading, atLeastOneError, data, filteredOverlay] as const;
  }, [queriesResults, overlayQuery, rangeZoom, proprietaMisurate]);

  const handleChange = useCallback(
    (value: string, from?: Moment | null, to?: Moment | null) => {
      if (ProprietaMisurataIntervals[value]) {
        setIntervalOrRange({
          type: "interval",
          key: value,
          helper: ProprietaMisurataIntervals[value],
        });
      } else if (value.startsWith("t-")) {
        const id = parseInt(value.split("-")[1]);
        const trasferimento = dispositivo?.trasferimentiBreadcrumbs?.find(
          (breadcrumbs) => {
            const t = breadcrumbs[breadcrumbs.length - 1];
            return t?.arco?.id === id;
          },
        );
        if (trasferimento) {
          setIntervalOrRange({
            type: "trasferimento",
            key: value,
            trasferimentoId: trasferimento[trasferimento.length - 1]?.arco?.id,
          });
        }
      } else if (value === "custom") {
        setIntervalOrRange({
          type: "custom",
          key: value,
          from: from ?? null,
          to: to ?? null,
        });
      }

      setRangeZoom([null, null]);
    },
    [dispositivo],
  );

  const dispositivoLabel = dispositivo
    ? `${getDispositivoTypeLabel(
        TipoDispositivoByTipoNodo[dispositivo.tipo],
      )} ${dispositivo.dispositivoMatricola}`
    : "";

  const handleClose = () => {
    onClose?.();
  };

  const canExportPdf = canQueryData && proprietaMisurate.length > 0;
  const [isExportingPdf, setIsExportingPdf] = useState(false);
  const handleExportPdf = () => {
    if (!canExportPdf || !dispositivo) {
      return;
    }

    setIsExportingPdf(true);
    generateReportProprietaMisurata(portaleConfig.id, dispositivo.id, {
      proprietaMisurateIds: proprietaMisurate.map((p) => p.id),
      from: baseParams?._from,
      to: baseParams?._to,
      trasferimentoId: baseParams?.idTrasferimento,
    })
      .then((response) => {
        const blob = new Blob([response.data], { type: "application/pdf" });
        const urlBlob = window.URL.createObjectURL(blob);

        // extract file name
        const contentDisposition = response.headers["content-disposition"];
        let fileName = t("proprietaMisurataHistory.reportFileName");

        if (contentDisposition) {
          const match = contentDisposition.match(/filename="?([^"]+)"?/);
          if (match && match[1]) {
            fileName = match[1];
          }
        }

        const link = document.createElement("a");
        link.href = urlBlob;
        link.setAttribute("download", fileName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        toast.success(t("proprietaMisurataHistory.toast.pdfSuccess"));
      })
      .catch(() => {
        toast.error(t("proprietaMisurataHistory.toast.pdfError"));
      })
      .finally(() => {
        setIsExportingPdf(false);
      });
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="xl">
      <DialogTitle>
        <strong>
          {t("proprietaMisurataHistory.titolo", {
            dispositivo: dispositivoLabel,
          })}
        </strong>
      </DialogTitle>
      <DialogContent>
        <Box display="grid" gridTemplateColumns="auto 1fr" gap={1}>
          <Box>
            <Typography sx={{ fontSize: "14px" }}>
              <strong>{t("proprietaMisurataHistory.campi.periodo")}</strong>
            </Typography>
          </Box>
          <Box>
            <Box
              display="flex"
              flexDirection="row"
              justifyContent="flex-start"
              alignItems="center"
              gap={1}
            >
              <Box>
                <FormControl
                  sx={{
                    minWidth:
                      intervalOrRange.type === "trasferimento" ? 400 : 120,
                  }}
                  size="small"
                >
                  <Select
                    value={intervalOrRange.key}
                    onChange={(e) => handleChange(e.target.value)}
                    disabled={isExportingPdf}
                  >
                    {Object.entries(ProprietaMisurataIntervals).map(
                      ([key, interval]) => {
                        return (
                          <MenuItem key={key} value={key}>
                            {interval.getLabel(t)}
                          </MenuItem>
                        );
                      },
                    )}
                    {dispositivo?.trasferimentiBreadcrumbs &&
                      dispositivo.trasferimentiBreadcrumbs.length >= 1 && (
                        <Divider />
                      )}

                    {(dispositivo?.trasferimentiBreadcrumbs || []).map(
                      (breadcrumbs) => {
                        const b = breadcrumbs[breadcrumbs.length - 1];
                        const value = `t-${b.arco.id}`;
                        const key = value;

                        const labelDa = b.arco.da
                          ? t("proprietaMisurataHistory.trasferimenti.da", {
                              data: moment(b.arco.da).format("YYYY-MM-DD"),
                            })
                          : "";
                        const labelA = b.arco.a
                          ? t("proprietaMisurataHistory.trasferimenti.a", {
                              data: moment(b.arco.a).format("YYYY-MM-DD"),
                            })
                          : "";
                        const label = [labelDa, labelA]
                          .filter((x) => x)
                          .join(" - ");

                        return (
                          <MenuItem key={key} value={key}>
                            <div>
                              {label}
                              <br />
                              <i>{b.arco.sorgenteAzienda?.nome || "-"}</i>
                            </div>
                          </MenuItem>
                        );
                      },
                    )}

                    <Divider />
                    <MenuItem value="custom">
                      {t("proprietaMisurataHistory.intervallo.personalizzato")}
                    </MenuItem>
                  </Select>
                </FormControl>
              </Box>

              {intervalOrRange.type === "custom" && (
                <>
                  <Typography sx={{ fontSize: "14px" }}>
                    {t("date.da")}
                  </Typography>
                  <DateTimeField
                    format="DD/MM/YYYY HH:mm"
                    onChange={(value) =>
                      handleChange("custom", value, intervalOrRange.to)
                    }
                    value={intervalOrRange.from}
                    disabled={isExportingPdf}
                  />
                  <Typography sx={{ fontSize: "14px" }}>
                    {t("date.a")}
                  </Typography>
                  <DateTimeField
                    format="DD/MM/YYYY HH:mm"
                    onChange={(value) =>
                      handleChange("custom", intervalOrRange.from, value)
                    }
                    value={intervalOrRange.to}
                    disabled={isExportingPdf}
                  />
                </>
              )}
            </Box>
          </Box>
          <Box sx={{ maxWidth: 240 }}>
            <ProprietaMisurataPicker
              proprietaMisurate={
                dispositivo?.dispositivoProprietaMisurate ?? []
              }
              value={proprietaMisurate}
              onChange={setProprietaMisurate}
              disabled={isExportingPdf}
            />
          </Box>
          <Box>
            <Box display="flex" flexDirection="column" flex={1} gap={1}>
              <Box
                flex={1}
                sx={{
                  border: "1px solid #d2d6da",
                  borderRadius: "8px",
                }}
              >
                <Box display="flex" flex={1}>
                  {selectedTrasferimentoObject &&
                  selectedTrasferimentoObject.arco.da &&
                  selectedTrasferimentoObject.arco.a ? (
                    <HistoryIntervalSlider
                      fromDate={selectedTrasferimentoObject.arco.da}
                      toDate={selectedTrasferimentoObject.arco.a}
                      value={[
                        rangeZoom[0] || selectedTrasferimentoObject.arco.da,
                        rangeZoom[1] || selectedTrasferimentoObject.arco.a,
                      ]}
                      onChange={([from, to]) => {
                        console.log(from, to);
                        setRangeZoom([from, to]);
                      }}
                    />
                  ) : null}
                </Box>

                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  sx={{ height: 500, position: "relative" }}
                >
                  {isLoading && (
                    <Box
                      sx={{
                        position: "absolute",
                        flex: 1,
                        backgroundColor: "rgba(0,0,0,0.05)",
                        width: "100%",
                        height: "100%",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      <Loading />
                    </Box>
                  )}
                  {!baseParams || proprietaMisurate.length <= 0 ? (
                    <Typography variant="body2">
                      <em>
                        {t("proprietaMisurataHistory.messaggi.seleziona")}
                      </em>
                    </Typography>
                  ) : error ? (
                    <LoadingError error={error} />
                  ) : (
                    <ProprietaMisurataHistoryChart
                      height={500}
                      data={chartData}
                      nexusOverlay={nexusOverlay}
                    />
                  )}
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          size="small"
          color="light"
          onClick={handleExportPdf}
          disabled={!canExportPdf || isExportingPdf}
          startIcon={
            isExportingPdf ? <CircularProgress size={20} /> : <DownloadIcon />
          }
        >
          {t("proprietaMisurataHistory.azioni.esportaPdf")}
        </Button>

        {onClose && (
          <Button
            size="small"
            color="light"
            onClick={handleClose}
            disabled={isExportingPdf}
          >
            {t("azioni.chiudi")}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

function ProprietaMisurataPicker({
  proprietaMisurate: _proprietaMisurate,
  value,
  onChange,
  disabled: _disabled = false,
}: {
  proprietaMisurate: ProprietaMisurataDto[];
  value: ProprietaMisurataDto[];
  onChange: (newValues: ProprietaMisurataDto[]) => void;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  const proprietaMisurate = useMemo(() => {
    return sortProprietaMisurate(_proprietaMisurate);
  }, [_proprietaMisurate]);

  const units = useMemo(() => {
    const units: string[] = [];
    for (const v of value) {
      const unit = v.type.unit ?? "scalar";
      if (!units.includes(unit)) {
        units.push(unit);
      }
    }
    return units;
  }, [value]);

  let atLeastOneDisabled = false;

  return (
    <FormControl component="fieldset" variant="standard">
      <FormLabel component="legend">
        {t("proprietaMisurataHistory.misure.titolo")}
      </FormLabel>
      <FormGroup>
        {proprietaMisurate.map((p) => {
          const checked = !!value.find((checkedP) => checkedP.id === p.id);
          const unit = p.type?.unit ?? "scalar";
          const disabled = !units.includes(unit) && units.length >= 2;
          if (disabled) {
            atLeastOneDisabled = true;
          }

          return (
            <FormControlLabel
              key={p.id}
              disabled={_disabled || disabled}
              control={
                <Checkbox
                  checked={checked}
                  sx={{ ml: 1.5 }}
                  onChange={() => {
                    if (checked) {
                      onChange(
                        value.filter((checkedP) => checkedP.id !== p.id),
                      );
                    } else {
                      onChange([...value, p]);
                    }
                  }}
                />
              }
              label={`${p.type.label} ${
                p.type.unit ? "(" + p.type.unit + ")" : ""
              }`}
            />
          );
        })}
      </FormGroup>
      {atLeastOneDisabled && (
        <FormHelperText>
          {t("proprietaMisurataHistory.misure.helper")}
        </FormHelperText>
      )}
    </FormControl>
  );
}

export default withOpeningReset(ProprietaMisurataHistoryDialog);
